import { useMutation, useQuery } from '@tanstack/react-query'
import { useRef } from 'react'
import AXIOS_INSTANCE from '../api/axiosInstance'
import { queryClient } from '../store/provider/TanStackProvider'
import {
  LabOrderCreatePayload,
  LabOrderUpdatePayload,
  LabResMock,
  LabResultNotificationRes,
  SingleLabOrderRes
} from '../types/lab.type'
import { nextNotificationPoll, type NotificationPollState } from '../utils/labResultEmail'

/**
 * Single lab order. Polls every 5 s (max ~2 min) while its latest result email is queued/sending,
 * so the status line updates without a reload.
 */
export const useSingleLabOrder = (id?: string) => {
  const pollState = useRef<NotificationPollState>({ id: null, startedAt: null })
  const { data, isLoading } = useQuery({
    enabled: !!id,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<SingleLabOrderRes>(`/lab_orders/${id}`)
      return data
    },
    queryKey: ['lab_order', { id }],
    refetchInterval: query => {
      const next = nextNotificationPoll(
        query.state.data?.data?.attributes?.latest_result_notification,
        pollState.current,
        Date.now()
      )
      pollState.current = next.state
      return next.interval
    }
  })
  return { singleLabOrder: data, singleLabOrderIsLoading: isLoading }
}

/** Add the patient's results email at result time — `PATCH /lab_orders/:id/patient_email` (E6). */
export const useAddPatientEmail = () => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({ email, id }: { email: string; id: string }) => {
      const { data } = await AXIOS_INSTANCE.patch<SingleLabOrderRes>(
        `lab_orders/${id}/patient_email`,
        { email },
        { headers: { 'Content-Type': 'application/json' } }
      )
      return data
    },
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['lab_order', { id }] })
      queryClient.invalidateQueries({ queryKey: ['lab_order'] })
      queryClient.invalidateQueries({ queryKey: ['patients'] })
    }
  })
  return { addPatientEmail: mutateAsync, isAddingPatientEmail: isPending }
}

type LabTypes = {
  patientId?: string
  id?: string
  created_at?: string
  status?: 'pending' | 'completed' | 'cancelled' | 'in_progress'
  limit?: string
  page?: string
  sortDir?: string
  query?: string
  encounterId?: string
  /** `queue=lab`: only orders with something for our lab to do (outside-only orders stay with the doctor). */
  queue?: 'lab'
}

/** Query string of `GET /lab_orders` for the given filters (exported for tests). */
export const buildLabOrderListParams = (arg?: LabTypes): Record<string, string> => {
  const { created_at, encounterId, limit, page, patientId, query, queue, sortDir, status } = arg ?? {}
  return {
    ...(patientId && { patient_id: patientId }),
    ...(encounterId && { encounter_id: encounterId }),
    ...(status && { status }),
    ...(sortDir && { sort_dir: sortDir }),
    ...(query && { query }),
    ...(limit && { 'page[size]': limit }),
    ...(page && { 'page[number]': page }),
    ...(created_at && { created_at }),
    ...(queue && { queue })
  }
}

export const useLab = (arg?: LabTypes) => {
  const { created_at, encounterId, id, limit, page, patientId, query, queue } = arg ?? {}
  const { sortDir, status } = arg ?? {}
  const { data: orders, isLoading } = useQuery({
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<LabResMock>('/lab_orders', { params: buildLabOrderListParams(arg) })
      return data
    },
    queryKey: ['lab_order', { created_at, encounterId, limit, page, patientId, query, queue, sortDir, status }]
  })

  const { singleLabOrder, singleLabOrderIsLoading } = useSingleLabOrder(id)

  const { mutateAsync: createLab } = useMutation({
    mutationFn: async (payload: LabOrderCreatePayload) => {
      const { data } = await AXIOS_INSTANCE.post('/lab_orders', payload, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lab_order'] })
    }
  })

  /** Pending bundle only — `PATCH /lab_orders/:id`. */
  const { mutateAsync: updateLabOrder } = useMutation({
    mutationFn: async ({ formVal, id: orderId }: { formVal: LabOrderUpdatePayload; id: string }) => {
      const { data } = await AXIOS_INSTANCE.patch(`/lab_orders/${orderId}`, formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: (_data, { id: orderId }) => {
      queryClient.invalidateQueries({ queryKey: ['lab_order', { id: orderId }] })
      queryClient.invalidateQueries({ queryKey: ['lab_order'] })
    }
  })

  const { mutateAsync: updateLab } = useMutation({
    mutationFn: async ({ formVal, id }: { formVal: any; id: string }) => {
      const { data } = await AXIOS_INSTANCE.patch(`orders/${id}`, formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      if (data.data.relationships.orderable.data.type === 'lab_test') {
        return data
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['lab_order', { id }] })
  })

  const { mutateAsync: deleteLab } = useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      await AXIOS_INSTANCE.delete(`orders/${id}`)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['lab_order'] })
  })

  const { mutateAsync: cancelAllOrder } = useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      await AXIOS_INSTANCE.patch(
        `lab_orders/${id}/cancel`,
        { data: { type: 'lab_order', attributes: { cancellation_reason: 'cancelled' } } },
        {
          headers: { 'Content-Type': 'application/vnd.api+json' }
        }
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lab_order', { id }] })
      queryClient.invalidateQueries({ queryKey: ['lab_order'] })
    }
  })

  const { mutateAsync: completeLabOrder } = useMutation({
    mutationFn: async ({ id, sendResultsToPatient }: { id: string; sendResultsToPatient?: boolean }) => {
      const body = {
        data: { type: 'lab_order' },
        ...(sendResultsToPatient !== undefined && { send_results_to_patient: sendResultsToPatient })
      }
      const { data } = await AXIOS_INSTANCE.patch(`lab_orders/${id}/complete`, body, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['lab_order', { id }] })
      queryClient.invalidateQueries({ queryKey: ['lab_order'] })
    }
  })

  /** Manual send / retry of the results email — `POST /lab_orders/:id/send_results` (202). */
  const { mutateAsync: sendLabResults, isPending: isSendingResults } = useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const { data } = await AXIOS_INSTANCE.post<LabResultNotificationRes>(`lab_orders/${id}/send_results`)
      return data
    },
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['lab_order', { id }] })
      queryClient.invalidateQueries({ queryKey: ['lab_order'] })
    }
  })

  return {
    cancelAllOrder,
    completeLabOrder,
    createLab,
    deleteLab,
    included: orders?.included,
    isSendingResults,
    singleLabOrder,
    singleLabOrderIsLoading,
    isLoading,
    lab_orders: orders?.data ?? [],
    orders,
    sendLabResults,
    updateLab,
    updateLabOrder
  }
}
